import { useCallback, useState } from 'react'

/**
 * An open/closed flag remembered per viewer in localStorage.
 *
 * For panels the user opens and closes (the home Chats card, the side panels),
 * where the choice is a convenience: if storage is unavailable (private
 * window, blocked site data) it silently falls back to `fallback` every load.
 *
 * `fallback` may be a function so a screen-size default is evaluated once, on
 * first render, and only when nothing is stored. A stored choice always wins,
 * which is the point: someone who opened the panel on a small screen should
 * not have it snap shut on the next visit.
 */
export function usePersistedToggle(
	key: string,
	fallback: boolean | (() => boolean)
): [boolean, (next: boolean) => void, () => void] {
	const [value, setValue] = useState<boolean>(() => {
		try {
			const raw = localStorage.getItem(key)
			if (raw === 'true') return true
			if (raw === 'false') return false
		} catch {
			// Storage unavailable: use the default.
		}
		return typeof fallback === 'function' ? fallback() : fallback
	})

	const set = useCallback(
		(next: boolean) => {
			setValue(next)
			try {
				localStorage.setItem(key, String(next))
			} catch {
				// Not remembered, but still applied for this visit.
			}
		},
		[key]
	)

	const toggle = useCallback(() => set(!value), [set, value])

	return [value, set, toggle]
}

/** True when the viewport is at least `px` wide. Safe where matchMedia is missing. */
export function viewportAtLeast(px: number): boolean {
	if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true
	return window.matchMedia(`(min-width: ${px}px)`).matches
}
